import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import enrollmentService from '../services/enrollmentService';
import resourceEnrollmentService from '../services/resourceEnrollmentService';
import CourseCard from '../components/CourseCard'; 
import { useAuth } from '../contexts/AuthContext';

const MyLearningPage = () => {
  const [allItems, setAllItems] = useState([]);
  const [filter, setFilter] = useState('ALL');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { user } = useAuth();

  useEffect(() => {
    const fetchLearningContent = async () => {
      try {
        setLoading(true);
        setError('');
        
        const [courseEnrollments, resourceEnrollments] = await Promise.all([
          enrollmentService.getMyEnrollments(),
          resourceEnrollmentService.getMyResourceEnrollments().catch(() => [])
        ]);
        
        // 1. Normalize Courses
        const courses = (courseEnrollments || []).map(enrollment => ({
          ...enrollment.course, 
          enrolledAt: enrollment.purchaseDate,
          expiryDate: enrollment.expiryDate,   // NEW: Map Expiry
          enrollmentStatus: enrollment.status, // NEW: Map Status (ACTIVE/EXPIRED)
          itemType: 'COURSE' 
        }));

        // 2. Normalize Resources
        const resources = (resourceEnrollments || []).map(enrollment => ({
          ...enrollment.resource, 
          enrolledAt: enrollment.enrolledAt,
          itemType: 'RESOURCE'
        }));
        
        const combined = [...courses, ...resources].sort((a, b) => 
          new Date(b.enrolledAt) - new Date(a.enrolledAt)
        );

        setAllItems(combined);

      } catch (err) {
        console.error("Error fetching learning data:", err);
        setError('Failed to load your learning list. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchLearningContent();
  }, []);

  const filteredItems = allItems.filter(item => {
    if (filter === 'ALL') return true;
    return item.itemType === filter;
  });

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center py-20">
          <div className="text-center">
            <div className="w-10 h-10 border-3 border-slate-200 border-t-slate-600 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-500 text-sm">Loading your content...</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
          <p className="font-semibold mb-1">Error</p>
          <p className="text-sm">{error}</p>
        </div>
      );
    }

    if (filteredItems.length === 0) {
      return (
        <div className="text-center bg-white p-12 rounded-xl border border-slate-200 mt-6">
          <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6">
             <span className="text-4xl">📚</span>
          </div>
          <h2 className="text-xl font-heading font-bold text-slate-900 mb-2">
            No items found
          </h2>
          <Link to="/courses" className="inline-flex items-center justify-center px-7 py-3.5 text-sm font-medium text-white bg-slate-900 rounded-lg hover:bg-slate-800 transition-all duration-200 shadow-sm">
            Explore Content
          </Link>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
        {filteredItems.map(item => (
          <CourseCard
            key={`${item.itemType}-${item.id}`}
            course={item}
            isEnrolled={true}
            type={item.itemType === 'COURSE' ? 'COURSE' : item.type}
          />
        ))}
      </div>
    );
  };

  const FilterButton = ({ label, value, count }) => (
    <button
      onClick={() => setFilter(value)}
      className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 border flex items-center gap-2 ${
        filter === value
          ? 'bg-slate-900 text-white border-slate-900'
          : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
      }`}
    >
      {label}
      <span className={`text-xs py-0.5 px-2 rounded-full ${
        filter === value ? 'bg-white/20' : 'bg-slate-100 text-slate-600'
      }`}>
        {count}
      </span>
    </button>
  );

  return (
    <div className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-slate-50 border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl font-heading font-bold text-slate-900 mb-4 tracking-tight">
              My Learning
            </h1>
            <p className="text-lg text-slate-600">
              Welcome back, {user?.fullName || 'Student'}!
            </p>
          </div>
        </div>
      </section>

      <section className="py-12 lg:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {!loading && !error && allItems.length > 0 && (
            <div className="flex flex-wrap justify-center gap-3 mb-8">
              <FilterButton label="All Content" value="ALL" count={allItems.length} />
              <FilterButton label="Courses" value="COURSE" count={allItems.filter(i => i.itemType === 'COURSE').length} />
              <FilterButton label="Resources" value="RESOURCE" count={allItems.filter(i => i.itemType === 'RESOURCE').length} />
            </div>
          )}
          {renderContent()}
        </div>
      </section>
    </div>
  );
};

export default MyLearningPage;